// src/App.tsx
import { useTranslation } from 'react-i18next';
import React, { Suspense } from 'react';
import axios from 'axios';
import { BrowserRouter as Router, Route, Routes, useLocation, Navigate } from 'react-router-dom';
import { SEARCH_RESULTS_PATH } from './config/site';
import { discoverySearchHasMinimumFilters } from './utils/prosDiscoverySearch';
import NoIndexRouteGuard from './components/SEO/NoIndexRouteGuard';
import LocaleRouteSync from './components/LocaleRouteSync';
import { localizedServicesRouteElements } from './routes/LocalizedServicesRoutes';
import { localizedLocaleHubRouteElements } from './routes/localizedLocaleHubRoutes';
import {
  frMetierShortCityRouteElements,
  frMetierShortRouteElements,
  localizedMetierShortCityRouteElements,
  localizedMetierShortRouteElements,
} from './routes/localizedMetierShortRoutes';
import RouteFallback from './components/RouteFallback';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { getNativeClientHeaders } from './config/apiConfig';
import { FeatureFlag, FeatureFlagsProvider, useFeatureFlags } from './contexts/FeatureFlagsContext';
import FeatureFlagGate from './components/FeatureFlagGate';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './App.css';
import { GoogleMapsProvider } from './contexts/GoogleMapsLoaderContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { GeoProvider } from './contexts/GeoContext';
import './styles/colors.css';
import PostHogPageViews from './components/PostHogPageViews';
import { GiftHandoffBootstrap } from './components/GiftHandoffBootstrap/GiftHandoffBootstrap';
import { NativeGiftDeepLink } from './components/native/NativeGiftDeepLink/NativeGiftDeepLink';
import PostHogGate from './components/PostHogGate';
import CookieConsentBanner from './components/CookieConsentBanner';
import NativePushBridge from './components/native/NativePushBridge/NativePushBridge';
import NativeBootGate from './components/native/NativeBootGate/NativeBootGate';
import { NativeErrorBoundary } from './components/native/NativeErrorBoundary/NativeErrorBoundary';
import { createRecoverableLazyLoader } from './utils/chunkErrorHandler';
import MobileAuthHandler from './components/MobileAuthHandler';
import { CartProvider } from './contexts/CartContext';
import { BookingAnotherSlotProvider } from './contexts/BookingAnotherSlotContext';
import { CartManager } from './components/Cart/CartManager';
import { LazyTwilioMessagingProvider } from './providers/LazyTwilioMessagingProvider';
import { AppLayoutProvider } from './contexts/AppLayoutContext';
import { ConsentProvider } from './contexts/ConsentContext';
import { useAppLayout } from './hooks/useAppLayout';
import { AppShell } from './layouts/AppShell';
import { getShellRouteContext } from './layouts/shellRouteContext';

// Parcours SEO / LCP — home en eager ; le reste lazy pour ne pas charger Leaflet/Twilio au boot natif
import HomeClassic from './pages/HomeClassic/HomeClassic';

const ProList = React.lazy(() => import('./pages/ProList/ProList'));
const DirectoryListingPage = React.lazy(
  () => import('./pages/DirectoryListing/DirectoryListingPage')
);
const ProPage = React.lazy(() => import('./pages/ProPage/ProPage'));
import { AuthLoginRoute, AuthSignupRoute } from './pages/Auth/AuthEntryRoutes';

// Routes lazy (réduit le bundle initial)
const MetierLanding = React.lazy(() => import('./pages/MetierLanding/MetierLanding'));
const MetierCityLanding = React.lazy(() => import('./pages/MetierLanding/MetierCityLanding'));
const ServicesHubPage = React.lazy(() => import('./pages/ServicesHub/ServicesHubPage'));
const ProfessionLegacyRedirect = React.lazy(
  () => import('./pages/MetierLanding/ProfessionLegacyRedirect')
);
const ContactPage = React.lazy(() => import('./pages/Contact/ContactPage'));
const AboutPage = React.lazy(() => import('./pages/About/About'));
const HowItWorksPage = React.lazy(() => import('./pages/HowItWorks/HowItWorksPage'));
const PasswordForgotten = React.lazy(() => import('./pages/PasswordForgotten/PasswordForgotten'));
const ThankYouPage = React.lazy(() => import('./pages/ThankYou/ThankYou'));
const GoogleAuthCallback = React.lazy(() => import('./components/SignInWithGoogle/GoogleAuthCallback'));
const EventDetail = React.lazy(() => import('./pages/EventDetail/EventDetail'));
const CheckoutValidation = React.lazy(() => import('./pages/CheckoutValidation/CheckoutValidation'));
const CheckoutCancel = React.lazy(() => import('./pages/CheckoutCancel/CheckoutCancel'));
const ResetPasswordPage = React.lazy(() => import('./pages/ResetPassword/ResetPasswordPage'));
const TermsOfUse = React.lazy(() => import('./pages/TermsOfUse/TermsOfUse'));
const PrivacyPolicy = React.lazy(() => import('./pages/PrivacyPolicy/PrivacyPolicy'));
const LegalPage = React.lazy(() => import('./pages/LegalPage/LegalPage'));
const MesForfaitsPage = React.lazy(
  createRecoverableLazyLoader(() => import('./pages/ForfaitsPage/MesForfaitsPage')),
);
const MesAbonnementsPage = React.lazy(
  createRecoverableLazyLoader(() => import('./pages/AbonnementsPage/MesAbonnementsPage')),
);
const PublicQuotePage = React.lazy(() => import('./pages/PublicQuotePage/PublicQuotePage'));
const GiftVoucherPage = React.lazy(() => import('./pages/GiftVoucherPage/GiftVoucherPage'));
const MesCadeauxPage = React.lazy(() => import('./pages/GiftVoucherPage/MesCadeauxPage'));
const QuotesPage = React.lazy(() => import('./pages/QuotesPage/QuotesPage'));
const EventPaymentSuccess = React.lazy(() => import('./pages/EventPaymentSuccess/EventPayementSuccess'));
const PaymentReturnPage = React.lazy(() => import('./pages/PaymentReturn/PaymentReturnPage'));
const ForfaitSuccessPage = React.lazy(() => import('./pages/ProDetailLegacy/ForfaitSuccesPage'));
const CreditsSuccessPage = React.lazy(() => import('./pages/ProDetailLegacy/CreditsSuccessPage'));
const CreditsPage = React.lazy(
  createRecoverableLazyLoader(() => import('./pages/CreditsPage/CreditsPage')),
);
const MessagingPage = React.lazy(() => import('./pages/Messaging/MessagingPage'));
const ProDetailLegacy = React.lazy(() => import('./pages/ProDetailLegacy/ProDetail'));
const EmbedBookingPage = React.lazy(() => import('./pages/EmbedBookingPage/EmbedBookingPage'));
const WidgetLabPage = React.lazy(() => import('./pages/WidgetLabPage/WidgetLabPage'));
const DaLabPage = React.lazy(() => import('./pages/DaLabPage/DaLabPage'));
const ServicePage = React.lazy(() => import('./pages/ServicePage/ServicePage'));
const BookingPage = React.lazy(() => import('./pages/BookingPage/BookingPage'));
const ForfaitDetailPage = React.lazy(() =>
  import('./pages/ForfaitDetailPage/ForfaitDetailPage').then((m) => ({ default: m.ForfaitDetailPage }))
);
const CreditPackDetailPage = React.lazy(() =>
  import('./pages/CreditPackDetailPage/CreditPackDetailPage').then((m) => ({
    default: m.CreditPackDetailPage,
  }))
);
const ClubSubscriptionPage = React.lazy(() =>
  import('./pages/ClubSubscriptionPage/ClubSubscriptionPage').then((m) => ({ default: m.ClubSubscriptionPage }))
);
const ClubInvitationPage = React.lazy(() =>
  import('./pages/ClubInvitationPage/ClubInvitationPage').then((m) => ({ default: m.ClubInvitationPage }))
);
const ClubSubscriptionSuccess = React.lazy(() =>
  import('./pages/ClubSubscriptionPage/ClubSubscriptionSuccess').then((m) => ({
    default: m.ClubSubscriptionSuccess,
  }))
);
const InvitationSignupPage = React.lazy(() => import('./pages/InvitationSignupPage/InvitationSignupPage'));
const AssociationPage = React.lazy(() => import('./pages/AssociationPage/AssociationPage'));
const CGVSignaturePage = React.lazy(() =>
  import('./pages/CGVSignaturePage/CGVSignaturePage').then((m) => ({ default: m.CGVSignaturePage }))
);
const DocumentViewPage = React.lazy(() =>
  import('./pages/DocumentViewPage/DocumentViewPage').then((m) => ({ default: m.DocumentViewPage }))
);
const DocumentSignaturePage = React.lazy(() =>
  import('./pages/DocumentSignaturePage/DocumentSignaturePage').then((m) => ({
    default: m.DocumentSignaturePage,
  }))
);
const BookingConfirmation = React.lazy(() =>
  import('./pages/BookingConfirmation/BookingConfirmation').then((m) => ({ default: m.BookingConfirmation }))
);
const BookingConfirmed = React.lazy(() => import('./pages/BookingConfirmed/BookingConfirmed'));
const BookingDeclined = React.lazy(() => import('./pages/BookingDeclined/BookingDeclined'));
const ForfaitPurchaseCGVPage = React.lazy(() => import('./pages/ForfaitPurchaseCGVPage'));
const CreditsPurchaseCGVPage = React.lazy(() => import('./pages/CreditsPurchaseCGVPage'));
const EmployeePinPage = React.lazy(() => import('./pages/EmployeePinPage/EmployeePinPage'));
const FavoritesPage = React.lazy(() => import('./pages/Favorites/FavoritesPage'));
const MesSuivisPage = React.lazy(() => import('./pages/Suivis/MesSuivisPage'));
const ForfaitBookingSuccess = React.lazy(() => import('./pages/ForfaitBookingSuccess/ForfaitBookingSuccess'));
const BoardingSuccess = React.lazy(() => import('./pages/BoardingSuccess/BoardingSuccess'));
const CheckoutPage = React.lazy(() => import('./pages/Checkout/CheckoutPage'));
const WidgetStripeReturnPage = React.lazy(
  () => import('./pages/WidgetStripeReturn/WidgetStripeReturnPage')
);
const AdminHandoffPage = React.lazy(() => import('./pages/AdminHandoff/AdminHandoffPage'));
const AnimalPage = React.lazy(() => import('./pages/Animal/AnimalPage'));
const AnimalHealthPage = React.lazy(() => import('./pages/Animal/AnimalHealthPage'));
const AnimalCarnetPublicPage = React.lazy(() => import('./pages/Animal/AnimalCarnetPublicPage'));
const ProfilePage = React.lazy(() => import('./pages/Profile/ProfilePage'));
const ProfileSettingsPage = React.lazy(() => import('./pages/Profile/ProfileSettingsPage'));
const ReservationsPage = React.lazy(() => import('./pages/Reservations/ReservationsPage'));
const FeaturesPage = React.lazy(() => import('./pages/Features/FeaturesPage'));
const PlanningReservationsPage = React.lazy(() => import('./pages/Features/pages/PlanningReservationsPage'));
const MessagingFeaturePage = React.lazy(() => import('./pages/Features/pages/MessagingFeaturePage'));
const PaiementFacturationPage = React.lazy(() => import('./pages/Features/pages/PaiementFacturationPage'));
const ForfaitsCreditsPage = React.lazy(() => import('./pages/Features/pages/ForfaitsCreditsPage'));
const CartesCadeauxPage = React.lazy(() => import('./pages/Features/pages/CartesCadeauxPage'));
const EmailsProPage = React.lazy(() => import('./pages/Features/pages/EmailsProPage'));
const GroupesClientsPage = React.lazy(() => import('./pages/Features/pages/GroupesClientsPage'));
const GestionClientsAnimauxPage = React.lazy(() => import('./pages/Features/pages/GestionClientsAnimauxPage'));
const StatistiquesPage = React.lazy(() => import('./pages/Features/pages/StatistiquesPage'));
const ProfilProfessionnelPage = React.lazy(() => import('./pages/Features/pages/ProfilProfessionnelPage'));
const PensionHebergementPage = React.lazy(() => import('./pages/Features/pages/PensionHebergementPage'));
const ApplicationMobilePage = React.lazy(() => import('./pages/Features/pages/ApplicationMobilePage'));
const BlogPage = React.lazy(() => import('./pages/Blog/BlogPage'));
const BlogArticlePage = React.lazy(() => import('./pages/Blog/BlogArticlePage'));
const NotificationsNative = React.lazy(
  () => import('./pages/Notifications/NotificationsNative'),
);
const LegacyProPathRedirect = React.lazy(
  () => import('./components/LegacyProPathRedirect'),
);

axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const nativeH = getNativeClientHeaders();
    if (Object.keys(nativeH).length > 0) {
      Object.assign(config.headers, nativeH);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

axios.interceptors.response.use(
  (response) => response,
  (error: unknown) => {
    const msg = error && typeof (error as Error).message === 'string' ? (error as Error).message : '';
    if (
      (msg && msg.includes("Unexpected token '<'")) ||
      (error instanceof SyntaxError && msg.includes('Unexpected token'))
    ) {
      return Promise.reject(
        new Error(
          "Le serveur a renvoyé du HTML au lieu de JSON. Vérifiez REACT_APP_API_BASE_URL (ou le proxy) et que l'API Rintintin est joignable."
        )
      );
    }
    return Promise.reject(error);
  }
);

const RINTINTIN_PRO_HOME = 'https://www.rintintin-pro.com/';

function ProsLegacyRedirect() {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  if (discoverySearchHasMinimumFilters(params)) {
    return <Navigate to={`${SEARCH_RESULTS_PATH}?${params.toString()}`} replace />;
  }
  if (typeof window !== 'undefined') {
    window.location.replace(RINTINTIN_PRO_HOME);
  }
  return null;
}

const AppContent = () => {
  const { t } = useTranslation('common');
  const location = useLocation();
  const { layout } = useAppLayout();
  const { isFeatureEnabled } = useFeatureFlags();
  const bookingAccessOn = isFeatureEnabled(FeatureFlag.BOOKING_ACCESS);
  const shellRoute = getShellRouteContext(
    location.pathname,
    location.search,
    layout === 'mobile'
  );

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  React.useEffect(() => {
    if (!shellRoute.shouldHideChrome) return;
    document.body.classList.add('embed-route');
    return () => document.body.classList.remove('embed-route');
  }, [shellRoute.shouldHideChrome]);

  if (!bookingAccessOn) {
    return (
      <div className='App' style={{ padding: '48px 24px', textAlign: 'center', maxWidth: 480, margin: '0 auto' }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 12 }}>{t('maintenance.title')}</h1>
        <p style={{ color: '#64748b' }}>{t('maintenance.body')}</p>
      </div>
    );
  }

  return (
    <div className={shellRoute.shouldHideChrome ? 'App App--embed' : 'App'}>
      <PostHogPageViews />
      <GiftHandoffBootstrap />
      <NativeGiftDeepLink />
      <CookieConsentBanner hidden={shellRoute.shouldHideChrome} />
      <NativePushBridge />
      <MobileAuthHandler />
      <NoIndexRouteGuard />
      <LocaleRouteSync />
      <AppShell route={shellRoute}>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path='/' element={<HomeClassic />} />
            <Route path='/services' element={<ServicesHubPage />} />
            <Route path='/services/' element={<ServicesHubPage />} />
            <Route path='/services/:metierSlug/:citySlug' element={<MetierCityLanding />} />
            <Route path='/services/:slug' element={<MetierLanding />} />
            {localizedServicesRouteElements({
              MetierLanding,
              MetierCityLanding,
              ServicesHubPage,
            })}
            {localizedMetierShortCityRouteElements({ MetierCityLanding })}
            {localizedMetierShortRouteElements({ MetierLanding })}
            {localizedLocaleHubRouteElements()}
            {frMetierShortCityRouteElements({ MetierCityLanding })}
            {frMetierShortRouteElements({ MetierLanding })}
            <Route path='/recherche' element={<FeatureFlagGate flag={FeatureFlag.PRO_DISCOVERY}><ProList /></FeatureFlagGate>} />
            <Route path='/annuaire/:slug' element={<FeatureFlagGate flag={FeatureFlag.BOOKING_DIRECTORY_LISTINGS}><DirectoryListingPage /></FeatureFlagGate>} />
            <Route path='/pros' element={<ProsLegacyRedirect />} />
            <Route path='/pros/*' element={<Navigate to='/' replace />} />
            <Route path='/profession' element={<Navigate to='/' replace />} />
            <Route path='/profession/:slug' element={<ProfessionLegacyRedirect />} />
            <Route
              path='/blog'
              element={
                <Suspense fallback={<RouteFallback />}>
                  <BlogPage />
                </Suspense>
              }
            />
            <Route
              path='/blog/category/:categorySlug'
              element={
                <Suspense fallback={<RouteFallback />}>
                  <BlogPage />
                </Suspense>
              }
            />
            <Route
              path='/blog/:slug'
              element={
                <Suspense fallback={<RouteFallback />}>
                  <BlogArticlePage />
                </Suspense>
              }
            />
            <Route path='/pro/:slug' element={<FeatureFlagGate flag={FeatureFlag.PRO_PAGE}><ProPage /></FeatureFlagGate>} />
            <Route path='/embed/booking/:slug' element={<FeatureFlagGate flag={FeatureFlag.BOOKING_WIDGET}><EmbedBookingPage /></FeatureFlagGate>} />
            <Route path='/embed/services/:slug' element={<FeatureFlagGate flag={FeatureFlag.BOOKING_WIDGET}><EmbedBookingPage /></FeatureFlagGate>} />
            <Route path='/dev/widget-lab' element={<WidgetLabPage />} />
            <Route path='/dev/da' element={<DaLabPage />} />
            <Route path='/pro/:slug/service/:serviceSlug' element={<FeatureFlagGate flag={FeatureFlag.SERVICE_PAGE}><ServicePage /></FeatureFlagGate>} />
            <Route path='/pro/:slug/book' element={<FeatureFlagGate flag={FeatureFlag.BOOKING_FLOW}><BookingPage /></FeatureFlagGate>} />
            <Route path='/pro/:slug/forfait/:forfaitSlug' element={<FeatureFlagGate flag={FeatureFlag.FORFAITS}><ForfaitDetailPage /></FeatureFlagGate>} />
            <Route path='/pro/:slug/credit-pack/:packSlug' element={<FeatureFlagGate flag={FeatureFlag.CREDITS}><CreditPackDetailPage /></FeatureFlagGate>} />
            <Route path='/pro/:slug/abonnement/:planId' element={<FeatureFlagGate flag={FeatureFlag.CLUB_SUBSCRIPTIONS}><ClubSubscriptionPage /></FeatureFlagGate>} />
            <Route path='/club-invitation/:token' element={<FeatureFlagGate flag={FeatureFlag.CLUB_SUBSCRIPTIONS}><ClubInvitationPage /></FeatureFlagGate>} />
            <Route path='/club-subscription-success' element={<FeatureFlagGate flag={FeatureFlag.CLUB_SUBSCRIPTIONS}><ClubSubscriptionSuccess /></FeatureFlagGate>} />
            <Route path='/pro-old/:slug' element={<ProDetailLegacy />} />
            <Route path='/event/:slug' element={<FeatureFlagGate flag={FeatureFlag.EVENTS}><EventDetail /></FeatureFlagGate>} />
            <Route path='/validated-checkout' element={<FeatureFlagGate flag={FeatureFlag.CHECKOUT_PAYMENTS}><CheckoutValidation /></FeatureFlagGate>} />
            <Route path='/cancel-checkout' element={<FeatureFlagGate flag={FeatureFlag.CHECKOUT_PAYMENTS}><CheckoutCancel /></FeatureFlagGate>} />
            <Route path='/forfait/success' element={<FeatureFlagGate flag={FeatureFlag.FORFAITS}><ForfaitSuccessPage /></FeatureFlagGate>} />
            <Route path='/forfait-ready' element={<FeatureFlagGate flag={FeatureFlag.FORFAITS}><ForfaitBookingSuccess /></FeatureFlagGate>} />
            <Route path='/forfait-purchase-cgv' element={<FeatureFlagGate flag={FeatureFlag.FORFAITS}><ForfaitPurchaseCGVPage /></FeatureFlagGate>} />
            <Route path='/credits-purchase-cgv' element={<FeatureFlagGate flag={FeatureFlag.CREDITS}><CreditsPurchaseCGVPage /></FeatureFlagGate>} />
            <Route path='/credits-purchase-success' element={<FeatureFlagGate flag={FeatureFlag.CREDITS}><CreditsSuccessPage /></FeatureFlagGate>} />
            <Route path='/boarding-success' element={<FeatureFlagGate flag={FeatureFlag.BOARDING_MODULE}><BoardingSuccess /></FeatureFlagGate>} />
            <Route path='/login' element={<AuthLoginRoute />} />
            <Route path='/admin-handoff' element={<AdminHandoffPage />} />
            <Route path='/password-forgotten' element={<PasswordForgotten />} />
            <Route path='/reset-password' element={<ResetPasswordPage />} />
            <Route path='/signup' element={<AuthSignupRoute />} />
            <Route path='/inscription' element={<InvitationSignupPage />} />
            <Route path='/animal' element={<AnimalPage />} />
            <Route path='/carnet/:token' element={<AnimalCarnetPublicPage />} />
            <Route path='/animals/:animalId/health' element={<FeatureFlagGate flag={FeatureFlag.ANIMAL_HEALTH}><AnimalHealthPage /></FeatureFlagGate>} />
            <Route path='/mes-forfaits' element={<FeatureFlagGate flag={FeatureFlag.FORFAITS}><MesForfaitsPage /></FeatureFlagGate>} />
            <Route path='/mes-abonnements' element={<FeatureFlagGate flag={FeatureFlag.CLUB_SUBSCRIPTIONS}><MesAbonnementsPage /></FeatureFlagGate>} />
            <Route path='/credits' element={<FeatureFlagGate flag={FeatureFlag.CREDITS}><CreditsPage /></FeatureFlagGate>} />
            <Route path='/mes-cadeaux' element={<MesCadeauxPage />} />
            <Route path='/messaging' element={<FeatureFlagGate flag={FeatureFlag.CHAT_FEATURE}><MessagingPage /></FeatureFlagGate>} />
            <Route path='/profile/settings' element={<ProfileSettingsPage />} />
            <Route path='/profile' element={<ProfilePage />} />
            <Route path='/favorites' element={<FeatureFlagGate flag={FeatureFlag.FAVORITES}><FavoritesPage /></FeatureFlagGate>} />
            <Route path='/reservations' element={<ReservationsPage />} />
            {/* Deep links pro → client (notifs séjour historiques /app/stay/:id) */}
            <Route
              path='/app/stay/:stayId'
              element={<LegacyProPathRedirect kind='stay' />}
            />
            <Route
              path='/app/booking/:bookingId'
              element={<LegacyProPathRedirect kind='booking' />}
            />
            <Route path='/mes-suivis' element={<FeatureFlagGate flag={FeatureFlag.CLIENT_REPORTS}><MesSuivisPage /></FeatureFlagGate>} />
            <Route path='/compte-rendus' element={<FeatureFlagGate flag={FeatureFlag.CLIENT_REPORTS}><MesSuivisPage /></FeatureFlagGate>} />
            <Route path='/quotes' element={<FeatureFlagGate flag={FeatureFlag.QUOTES}><QuotesPage /></FeatureFlagGate>} />
            <Route path='/notifications' element={<NotificationsNative />} />
            <Route path='/public/quote/:quoteId' element={<FeatureFlagGate flag={FeatureFlag.QUOTES}><PublicQuotePage /></FeatureFlagGate>} />
            <Route path='/cadeau/:code' element={<GiftVoucherPage />} />
            <Route path='/booking-confirmation' element={<BookingConfirmation />} />
            <Route path='/thank-you' element={<ThankYouPage />} />
            <Route path='/contact' element={<ContactPage />} />
            <Route path='/legal' element={<LegalPage />} />
            <Route path='/terms-of-use' element={<TermsOfUse />} />
            <Route path='/privacy-policy' element={<PrivacyPolicy />} />
            <Route path='/about' element={<AboutPage />} />
            <Route path='/comment-ca-marche' element={<HowItWorksPage />} />
            <Route path='/association/:slug' element={<FeatureFlagGate flag={FeatureFlag.ASSOCIATION_PAGE}><AssociationPage /></FeatureFlagGate>} />
            <Route path='/fonctionnalites' element={<FeaturesPage />} />
            <Route path='/fonctionnalites/planning-reservations' element={<PlanningReservationsPage />} />
            <Route path='/fonctionnalites/messagerie' element={<MessagingFeaturePage />} />
            <Route path='/fonctionnalites/paiement-facturation' element={<PaiementFacturationPage />} />
            <Route path='/fonctionnalites/forfaits-credits' element={<ForfaitsCreditsPage />} />
            <Route path='/fonctionnalites/cartes-cadeaux' element={<CartesCadeauxPage />} />
            <Route path='/fonctionnalites/emails' element={<EmailsProPage />} />
            <Route path='/fonctionnalites/groupes-clients' element={<GroupesClientsPage />} />
            <Route path='/fonctionnalites/gestion-clients-animaux' element={<GestionClientsAnimauxPage />} />
            <Route path='/fonctionnalites/statistiques' element={<StatistiquesPage />} />
            <Route path='/fonctionnalites/profil-professionnel' element={<ProfilProfessionnelPage />} />
            <Route path='/fonctionnalites/pension-hebergement' element={<PensionHebergementPage />} />
            <Route path='/fonctionnalites/application-mobile' element={<ApplicationMobilePage />} />
            <Route path='/auth/google/callback' element={<GoogleAuthCallback />} />
            <Route path='/event-payment-success' element={<FeatureFlagGate flag={FeatureFlag.EVENTS}><EventPaymentSuccess /></FeatureFlagGate>} />
            {/* #1160 : retours Stripe Checkout (facture / demande de paiement) — URL de payment_controller.py */}
            <Route path='/payment-success' element={<PaymentReturnPage kind='invoice' outcome='success' />} />
            <Route path='/payment-cancel' element={<PaymentReturnPage kind='invoice' outcome='cancel' />} />
            <Route path='/payment-request-success' element={<PaymentReturnPage kind='request' outcome='success' />} />
            <Route path='/payment-request-cancel' element={<PaymentReturnPage kind='request' outcome='cancel' />} />
            <Route path='/cgv-accept/:token' element={<FeatureFlagGate flag={FeatureFlag.DOCUMENTS_CGV}><CGVSignaturePage /></FeatureFlagGate>} />
            <Route path='/documents/view/:token' element={<FeatureFlagGate flag={FeatureFlag.DOCUMENTS_CGV}><DocumentViewPage /></FeatureFlagGate>} />
            <Route path='/document-sign/:token' element={<DocumentSignaturePage />} />
            <Route path='/confirm-booking/:token' element={<FeatureFlagGate flag={FeatureFlag.BOOKING_TOKEN_ACTIONS}><BookingConfirmed /></FeatureFlagGate>} />
            <Route path='/decline-booking/:token' element={<FeatureFlagGate flag={FeatureFlag.BOOKING_TOKEN_ACTIONS}><BookingDeclined /></FeatureFlagGate>} />
            <Route path='/pro/:proId/pin-management' element={<EmployeePinPage />} />
            <Route path='/checkout' element={<FeatureFlagGate flag={FeatureFlag.CHECKOUT_PAYMENTS}><CheckoutPage /></FeatureFlagGate>} />
            <Route path='/widget-stripe-return' element={<FeatureFlagGate flag={FeatureFlag.BOOKING_WIDGET}><WidgetStripeReturnPage /></FeatureFlagGate>} />
          </Routes>
        </Suspense>
      </AppShell>
      <CartManager />
      <ToastContainer
        position='top-right'
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable={false}
        pauseOnHover
        theme='light'
        style={{ zIndex: 9999, marginTop: '80px' }}
      />
    </div>
  );
};

const MessagingProviderWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const { isFeatureEnabled } = useFeatureFlags();
  const chatOn = isFeatureEnabled(FeatureFlag.CHAT_FEATURE);
  const userIdentity = user?.user_id ? `user_${user.user_id}` : '';

  if (!chatOn || !isAuthenticated || !userIdentity) {
    return <>{children}</>;
  }

  return <LazyTwilioMessagingProvider identity={userIdentity}>{children}</LazyTwilioMessagingProvider>;
};

const App: React.FC = () => {
  return (
    <LanguageProvider>
      <GeoProvider>
        <Router>
          <ConsentProvider>
            <PostHogGate>
              <FeatureFlagsProvider>
                <AppLayoutProvider>
                  <AuthProvider>
                    <NativeErrorBoundary>
                      <NativeBootGate>
                        <GoogleMapsProvider>
                          <CartProvider>
                            <BookingAnotherSlotProvider>
                              <MessagingProviderWrapper>
                                <AppContent />
                              </MessagingProviderWrapper>
                            </BookingAnotherSlotProvider>
                          </CartProvider>
                        </GoogleMapsProvider>
                      </NativeBootGate>
                    </NativeErrorBoundary>
                  </AuthProvider>
                </AppLayoutProvider>
              </FeatureFlagsProvider>
            </PostHogGate>
          </ConsentProvider>
        </Router>
      </GeoProvider>
    </LanguageProvider>
  );
};

export default App;
